import { useMemo } from 'react';
import useSWR from 'swr';
import { usePolyAuth } from './PolyAuthContext';

/**
 * Hook to fetch meeting continuity data (last-met time, meeting count)
 * for a batch of participants within the current account.
 *
 * @param {Array} participants - Array of participant objects from the Sidebar
 * @param {string} actualMeetingId - The actual meeting ID to exclude from counts
 * @returns {{ continuity: null|Object, accountName: string, isLoading: boolean }}
 *   - continuity is null when auth is unavailable (anonymous/error)
 *   - continuity is {} when queried OK but no prior meetings for anyone
 *   - continuity maps poly_user_id -> { last_met_time, meeting_count }
 */
export const useMeetingContinuity = (participants, actualMeetingId) => {
  const { api_client, accountId, isAuthenticated } = usePolyAuth();

  // Extract, dedupe, filter, and sort poly_user_ids
  const sortedIds = useMemo(() => {
    if (!participants?.length) return [];
    const ids = new Set();
    for (const p of participants) {
      const id = p.pslUser?.poly_user_id;
      if (id && id !== 'unknown-00000') {
        ids.add(id);
      }
    }
    return [...ids].sort();
  }, [participants]);

  // SWR key: null when we can't/shouldn't query.
  // Includes actualMeetingId so the cache invalidates when it becomes available.
  const swrKey = useMemo(() => {
    if (!isAuthenticated || !accountId || !sortedIds.length) {
      console.debug('[continuity] SWR key null:', {
        isAuthenticated,
        accountId,
        sortedIdsCount: sortedIds.length,
        participantsCount: participants?.length,
      });
      return null;
    }
    const key = `continuity-${accountId}-${actualMeetingId || 'none'}-${sortedIds.join(',')}`;
    console.debug('[continuity] SWR key:', key);
    return key;
  }, [isAuthenticated, accountId, actualMeetingId, sortedIds]);

  const fetcher = async () => {
    const payload = {
      poly_user_ids: sortedIds,
      account_id: parseInt(accountId, 10),
      exclude_meeting_id: actualMeetingId || undefined,
    };
    console.debug('[continuity] Fetching:', payload);
    const response = await api_client.post('/v2/logs/continuity', payload);
    console.debug('[continuity] Response:', response.data);
    return response.data;
  };

  const { data, error, isLoading } = useSWR(swrKey, fetcher, {
    refreshInterval: 0,
    dedupingInterval: 30000,
    revalidateOnFocus: false,
    errorRetryCount: 1,
  });

  // On error or no auth, return null continuity (anonymous state)
  if (error || !swrKey) {
    if (error) console.warn('[continuity] Error fetching:', error);
    return { continuity: null, accountName: '', isLoading: false };
  }

  return {
    continuity: data?.continuity ?? null,
    accountName: data?.account_name ?? '',
    isLoading,
  };
};

export default useMeetingContinuity;
